/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Flex, Text, Spinner, Dropdown } from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import {
  ContractEditModeType,
  ContractLoadingType,
} from "~/components/Contract/ContractItem";
import { EditButton } from "~/components/InlineEdit";
import { contractTypeOptions } from "~/constants";
import { DurationType } from "~/models";
import { useClickOutside } from "~/tools";

type ContractInlineDurationSelectorProps = {
  durationType: string;
  setDurationType: React.Dispatch<React.SetStateAction<string>>;
  property: "durationType";
  isLoading: ContractLoadingType;
  setIsLoading: React.Dispatch<React.SetStateAction<ContractLoadingType>>;
  isEditMode: ContractEditModeType;
  setIsEditMode: React.Dispatch<React.SetStateAction<ContractEditModeType>>;
  canEdit?: boolean;
};

function ContractInlineDurationSelector({
  durationType,
  setDurationType,
  property,
  isLoading,
  isEditMode,
  setIsEditMode,
  canEdit = true,
}: ContractInlineDurationSelectorProps) {
  const wrapperRef = React.useRef(null);

  useClickOutside(
    wrapperRef,
    () => {
      setIsEditMode((prevState) => ({ ...prevState, [property]: false }));
    },
    isEditMode[property]
  );

  const EditForm = (
    <Flex ref={wrapperRef} css={{ maxWidth: "11rem" }}>
      {durationType && (
        <Dropdown
          onChange={(value: string) => {
            setDurationType(value as DurationType);
            setIsEditMode((prevState) => ({ ...prevState, [property]: false }));
          }}
          value={durationType}
          options={contractTypeOptions}
          name="Contract Type"
        />
      )}
    </Flex>
  );
  const DisplayText = (
    <>
      <Text as="div" m="0">
        <FlexContainer>
          {contractTypeOptions?.find((x) => x.value === durationType)?.label}
        </FlexContainer>
      </Text>
      {canEdit && (
        <EditButton
          onClick={() =>
            setIsEditMode((prevState) => ({ ...prevState, [property]: true }))
          }
        />
      )}
    </>
  );

  if (isLoading[property]) {
    return (
      <Flex>
        <Spinner size="sm" />
        <Text weight="regular" tag="div" css={{ marginLeft: "1rem" }}>
          Loading ...
        </Text>
      </Flex>
    );
  }

  if (isEditMode[property]) {
    return EditForm;
  }

  return <FlexContainer align="center">{DisplayText}</FlexContainer>;
}

export { ContractInlineDurationSelector };
